import { useCallback, useEffect, useRef, useState } from 'react'
import { MAX_FEEDBACK_ATTACHMENTS } from '@/lib/feedbackAttachments'

/**
 * A feedback form's attachments, plus a shake screenshot that may still be
 * rendering when the form opens. `collect()` waits for that screenshot (it's
 * bounded by the capture's own timeout) unless the user removed it.
 */
export function useFeedbackAttachments(pendingScreenshot?: Promise<Blob | null>) {
  const [attachments, setAttachments] = useState<Blob[]>([])
  const [pending, setPending] = useState(Boolean(pendingScreenshot))
  const dismissed = useRef(false)

  useEffect(() => {
    if (!pendingScreenshot) return
    let live = true
    void pendingScreenshot.then(blob => {
      if (!live || dismissed.current) return
      setPending(false)
      if (blob) setAttachments(prev => [blob, ...prev].slice(0, MAX_FEEDBACK_ATTACHMENTS))
    })
    return () => {
      live = false
    }
  }, [pendingScreenshot])

  const dismissPending = useCallback(() => {
    dismissed.current = true
    setPending(false)
  }, [])

  const collect = useCallback(async (): Promise<Blob[]> => {
    if (!pending || !pendingScreenshot) return attachments
    const blob = await pendingScreenshot
    return blob ? [blob, ...attachments].slice(0, MAX_FEEDBACK_ATTACHMENTS) : attachments
  }, [attachments, pending, pendingScreenshot])

  return { attachments, setAttachments, pending, dismissPending, collect }
}
